import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { defaultOptions, RouletteOption } from ".";

const RouletteSettings = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState<RouletteOption[]>(defaultOptions);

  // localStorage에서 데이터 로드
  useEffect(() => {
    const savedOptions = localStorage.getItem("rouletteOptions");
    if (savedOptions) {
      try {
        const parsedOptions = JSON.parse(savedOptions);
        setOptions(parsedOptions);
      } catch (error) {
        console.error("Error parsing saved options:", error);
        setOptions(defaultOptions);
      }
    }
  }, []);

  // localStorage에 데이터 저장
  const saveOptions = (newOptions: RouletteOption[]) => {
    setOptions(newOptions);
    localStorage.setItem("rouletteOptions", JSON.stringify(newOptions));
  };

  // 수량 업데이트
  const updateQuantity = (id: string, newQuantity: number) => {
    const updatedOptions = options.map((option) =>
      option.id === id
        ? { ...option, dailyLimit: Math.max(0, newQuantity) }
        : option
    );
    saveOptions(updatedOptions);
  };

  // 현재 뽑힌 수량 리셋
  const resetCurrentCount = (id: string) => {
    const updatedOptions = options.map((option) =>
      option.id === id ? { ...option, currentCount: 0 } : option
    );
    saveOptions(updatedOptions);
  };

  // 모든 현재 뽑힌 수량 리셋
  const resetAllCurrentCounts = () => {
    const updatedOptions = options.map((option) => ({
      ...option,
      currentCount: 0,
    }));
    saveOptions(updatedOptions);
  };

  // 기본값으로 리셋
  const resetToDefaults = () => {
    saveOptions(defaultOptions);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/roulette")}
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <span>←</span>
            <span>Retour à la roulette</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
            Paramètres de la Roulette
          </h1>
          <div className="w-32"></div> {/* Spacer for centering */}
        </div>

        {/* Controls */}
        <div className="mb-6 flex flex-wrap gap-4 justify-center">
          <button
            onClick={resetAllCurrentCounts}
            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg shadow-md transition-colors"
          >
            Réinitialiser tous les compteurs
          </button>
          <button
            onClick={resetToDefaults}
            className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md transition-colors"
          >
            Restaurer les valeurs par défaut
          </button>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {options.map((option) => {
            const remainingQuantity = option.dailyLimit - option.currentCount;
            const isOutOfStock = remainingQuantity <= 0;

            return (
              <div
                key={option.id}
                className={`bg-white rounded-lg shadow-lg p-6 border-2 ${
                  isOutOfStock ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              >
                {/* Color indicator and name */}
                <div className="flex items-center space-x-3 mb-4">
                  <div
                    className="w-6 h-6 rounded-full border-2 border-gray-300"
                    style={{ backgroundColor: option.color }}
                  ></div>
                  <h3 className="text-lg font-bold text-gray-800 flex-1">
                    {option.name}
                  </h3>
                </div>

                {/* Stock status */}
                <div className="mb-4">
                  <div
                    className={`text-sm font-medium ${
                      isOutOfStock ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {isOutOfStock
                      ? "Rupture de stock"
                      : `${remainingQuantity} restant(s)`}
                  </div>
                  <div className="text-xs text-gray-500">
                    Tiré: {option.currentCount} / {option.dailyLimit}
                  </div>
                </div>

                {/* Quantity controls */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantité totale:
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          updateQuantity(option.id, option.dailyLimit - 1)
                        }
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-600"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={option.dailyLimit}
                        onChange={(e) =>
                          updateQuantity(
                            option.id,
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="w-20 px-2 py-1 text-center border border-gray-300 rounded-md"
                        min="0"
                      />
                      <button
                        onClick={() =>
                          updateQuantity(option.id, option.dailyLimit + 1)
                        }
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-600"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Reset current count button */}
                  <button
                    onClick={() => resetCurrentCount(option.id)}
                    disabled={option.currentCount === 0}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      option.currentCount === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    Réinitialiser le compteur
                  </button>
                </div>

                {/* Weight info */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Poids de tirage: {option.weight}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Résumé</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {options.reduce((sum, opt) => sum + opt.dailyLimit, 0)}
              </div>
              <div className="text-sm text-gray-600">Total des articles</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {options.reduce(
                  (sum, opt) => sum + (opt.dailyLimit - opt.currentCount),
                  0
                )}
              </div>
              <div className="text-sm text-gray-600">Articles restants</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">
                {options.reduce((sum, opt) => sum + opt.currentCount, 0)}
              </div>
              <div className="text-sm text-gray-600">Articles tirés</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {
                  options.filter(
                    (opt) => opt.dailyLimit - opt.currentCount <= 0
                  ).length
                }
              </div>
              <div className="text-sm text-gray-600">En rupture</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouletteSettings;
